import React, { useState, useEffect } from 'react';
import { connect, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Container, Row, Col, Card, CardBody, CardTitle, Alert } from 'reactstrap';
import config from '../../../../../config';
import TableBuilder from '../../../../../shared/components/table/TableBuilder';

function AcountStatusAll() {
  const user = useSelector(store => store.authentication.user);
  const [warnings, setWarnings] = useState([]);
  const [accountsData, setAccountsData] = useState([]);
  const [loading, setLoading] = useState(true);

  const colorChange = (params) => {
    // Check if value exists and is a valid color string
    if (params.value && typeof params.value === 'string' && params.value.trim() !== '') {
      const colorValue = params.value.toLowerCase().trim();
      // Only apply styling for valid color values
      if (['green', 'yellow', 'red', 'blue', 'gray', 'grey'].includes(colorValue)) {
        const textColor = colorValue === 'yellow' ? 'black' : 'white';
        return { color: textColor, backgroundColor: colorValue, textAlign: 'center' };
      }
    }
    return { textAlign: 'center' };
  };

  // Fetch data from the API once
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const headerWithAuth = {
          ...config.jsonHeader,
          authorization: `JWT ${user.jwtToken}`,
        };
        const response = await fetch(`${config.serverUrl}/accounts/api-status/all-active`, {
          method: 'GET',
          headers: headerWithAuth,
        });
        const data = await response.json();

        // Extract warnings
        if (data.warnings && Array.isArray(data.warnings)) {
          setWarnings(data.warnings);
        }

        // Extract accounts data for the table
        if (data.accounts && Array.isArray(data.accounts)) {
          setAccountsData(data.accounts);
        } else if (Array.isArray(data)) {
          // Handle case where data is directly an array
          setAccountsData(data);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user.jwtToken]);

  const columnDefs = [
    { field: 'accountId', headerName: 'Account ID' },
    { field: 'name', headerName: 'Name' },
    { field: 'status', headerName: 'Status' },
    {
      headerName: 'Amazon',
      children: [
        { field: 'accountType', headerName: 'Account Type' },
        { field: 'region', headerName: 'Region' },
        { field: 'activeAmzAdApi', headerName: 'AMZ AD-API', cellStyle: (params) => colorChange(params) },
        { field: 'activeAmzSpApi', headerName: 'AMZ SP-API', cellStyle: (params) => colorChange(params) },
      ],
    },
    {
      headerName: 'Walmart',
      children: [
        { field: 'wmAdvertiserId', headerName: 'Advertiser ID' },
        { field: 'wmSellerId', headerName: 'Seller ID' },
        { field: 'activeWmAdApi', headerName: 'WM AD-API', cellStyle: (params) => colorChange(params) },
        { field: 'activeWmMpApi', headerName: 'WM MP-API', cellStyle: (params) => colorChange(params) },
        { field: 'activeWmSuApi', headerName: 'WM SU-API', cellStyle: (params) => colorChange(params) },
      ],
    },
    {
      headerName: 'Google',
      children: [
        { field: 'googleCustomerId', headerName: 'Google Customer ID' },
        { field: 'googleLimitlessManagerAccountId', headerName: 'Limitless Manager Account ID' },
      ],
    },

  ];

  const defaultColDef = {
    sortable: true,
    filter: true,
    resizable: true,
  };

  return (
    <Container>
      <Row>
        <Col md={12}>
          {!loading && (
            <TableBuilder
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              rowData={accountsData}
              parentControlledLoad
              showDatePicker={false}
            />
          )}
        </Col>
      </Row>
      {warnings.length > 0 && (
        <Row className="mt-4">
          <Col md={12}>
            <Card>
              <CardBody>
                <CardTitle tag="h5">API Warnings</CardTitle>
                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {warnings.map((warning) => (
                    <Alert color="warning" key={`warning-${warning}`} className="mb-2">
                      <small style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {warning}
                      </small>
                    </Alert>
                  ))}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}
    </Container>
  );
}

AcountStatusAll.propTypes = {
  user: PropTypes.shape({
    jwtToken: PropTypes.string.isRequired,
  }).isRequired,
};

const mapStateToProps = (state) => ({
  user: state.authentication.user,
});

export default connect(mapStateToProps)(AcountStatusAll);
